import {AxiosResponse} from 'axios';
import {enqueueSnackbar} from 'notistack';

import {
  API_BASE_URL,
  SUCCESS_ASSIGN_ASSET,
  TOKEN_EXPIRED,
  ERROR_ASSIGN_ASSET,
  SUCCESS_DELETE_ASSIGNMENT,
  ERROR_DELETE_ASSIGNMENT,
  ERROR_CHANGE_ASSET,
  ERROR_DELETE_ASSET,
  INFO_CHANGE_ASSET,
  MESSAGE_DURATION_LONG,
  SUCCESS_DELETE_ASSET,
  UNABLE_DELETE_ASSET,
  UPLOAD_ABORTED,
  UPLOAD_ERROR,
  UPLOAD_FILEUPLOAD_FINISHED,
  ASSET_METADATA_FIELDS,
  ERROR,
  NO_RESULT_METADATA,
  SUCCESS_CLEAR_MEDIAINBOX,
  SUCCESS_CLEAR_FAVORITES,
  ASSET_UNFAVORITE_SUCCESS,
  ASSET_FAVORITE_SUCCESS,
} from '../../constants';
import {
  AssetFavoriteRequestDto,
  HasAssetFavoritesResponseDto,
  ChangeAssetDataRequestDto,
  SearchAssetInformationDto,
  StructureAssignRequestDto,
  StructureResponseItemDto,
} from '../../models';
import {post, del, get} from '../api';

export function assignAsset(
  token: string,
  products: SearchAssetInformationDto[],
  structureGroup: StructureResponseItemDto,
) {
  const ids: string[] = [];
  products.map(product => ids.push(product.id));
  const body: StructureAssignRequestDto = {assets: ids};
  post(`${API_BASE_URL}/asset/assignStructure/${structureGroup.id}`, {
    data: JSON.stringify(body),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(_res => {
      enqueueSnackbar(SUCCESS_ASSIGN_ASSET, {variant: 'success'});
    })
    .catch(err => {
      switch (err?.response?.status) {
        case 401:
          enqueueSnackbar(TOKEN_EXPIRED, {variant: 'error'});
          break;
        default:
          enqueueSnackbar(ERROR_ASSIGN_ASSET, {variant: 'error'});
      }
    });
}

export function deleteAssetAssignment(
  token: string,
  product: string,
  structureGroupId: string,
  reloadMetaData: () => void,
) {
  del(`${API_BASE_URL}/asset/deleteAssignment/${product}/${structureGroupId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(_res => {
      reloadMetaData();
      enqueueSnackbar(SUCCESS_DELETE_ASSIGNMENT, {variant: 'success'});
    })
    .catch(err => {
      switch (err?.response?.status) {
        case 401:
          enqueueSnackbar(TOKEN_EXPIRED, {variant: 'error'});
          break;
        default:
          enqueueSnackbar(ERROR_DELETE_ASSIGNMENT, {variant: 'error'});
      }
    });
}

export function changeAsset(
  token: string,
  id: string,
  changes: ChangeAssetDataRequestDto[],
  reload?,
) {
  post(`${API_BASE_URL}/asset/change/${id}`, {
    data: JSON.stringify(changes),
    responseType: 'json',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      if (res.status === 200) {
        enqueueSnackbar(INFO_CHANGE_ASSET, {
          variant: 'success',
        });
        if (reload) {
          reload();
        }
      }
    })
    .catch(err => {
      if (err.response && err.response.status === 422) {
        const errorMessage = err.response.data;
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          autoHideDuration: MESSAGE_DURATION_LONG,
        });
      } else {
        enqueueSnackbar(ERROR_CHANGE_ASSET, {
          variant: 'error',
          autoHideDuration: MESSAGE_DURATION_LONG,
        });
      }
    });
}

export function deleteAsset(token: string, id: string, reload: () => void) {
  del(`${API_BASE_URL}/asset/${id}`, {
    responseType: 'json',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      if (res.status === 200) {
        enqueueSnackbar(SUCCESS_DELETE_ASSET, {variant: 'success'});
        reload();
      }
    })
    .catch(err => {
      console.error(err);
      if (err.response && err.response.status === 422) {
        enqueueSnackbar(UNABLE_DELETE_ASSET, {variant: 'warning'});
      } else {
        enqueueSnackbar(ERROR_DELETE_ASSET, {variant: 'error'});
      }
    });
}

export function uploadPreview(
  preview: File,
  assetId: string,
  token: string,
  setUploading: (value: boolean) => void,
  resetDialog: () => void,
  reset: boolean,
) {
  const formData = new FormData();
  const additionalParameter = `?manualPreview=${!reset}`;
  formData.append(`preview`, preview, preview.name);

  post(`${API_BASE_URL}/asset/addPreview/${assetId}${additionalParameter}`, {
    data: formData,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'json',
  })
    .then(_response => {
      setUploading(false);
      enqueueSnackbar(UPLOAD_FILEUPLOAD_FINISHED, {
        variant: 'success',
      });
      resetDialog();
    })
    .catch(error => {
      if (error.code === 'ERR_CANCELED') {
        enqueueSnackbar(UPLOAD_ABORTED, {
          variant: 'info',
        });
      } else {
        enqueueSnackbar(UPLOAD_ERROR, {
          variant: 'error',
        });
      }
      setUploading(false);
    });
}

export function loadMetaData(
  token: string,
  actualAsset: string,
  setMetaData,
  logout: () => void,
) {
  let data;
  post(`${API_BASE_URL}/asset/metadata/${actualAsset}`, {
    data: JSON.stringify(ASSET_METADATA_FIELDS),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(res => {
      if (res.data) {
        data = res.data;
        setMetaData(data);
      } else {
        enqueueSnackbar(NO_RESULT_METADATA, {variant: 'warning'});
        setMetaData(undefined);
      }
    })
    .catch(err => {
      switch (err?.response?.status) {
        case 401:
          logout();
          break;
        default:
          enqueueSnackbar(ERROR, {variant: 'error'});
      }
    });
}

export function loadLogData(
  token: string,
  assetId: string,
  setLogData: (data: unknown) => void,
  setLoading: (loading: boolean) => void,
) {
  get(`${API_BASE_URL}/asset/logs/${assetId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(res => {
      if (res.data) {
        setLogData(res.data);
      }
    })
    .catch(err => {
      console.error('Error loading log data:', err);
    })
    .finally(() => {
      setLoading(false);
    });
}

export function clearMediaInbox(token: string, onRequestComplete: () => void) {
  del(`${API_BASE_URL}/asset/clearMediaInbox`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(_res => {
      enqueueSnackbar(SUCCESS_CLEAR_MEDIAINBOX, {variant: 'success'});
      onRequestComplete();
    })
    .catch(_err => {
      enqueueSnackbar(ERROR, {variant: 'error'});
    });
}

export function clearFavoriteAssets(
  token: string,
  onRequestComplete: () => void,
) {
  del(`${API_BASE_URL}/asset/clearFavoriteAssets`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(_res => {
      enqueueSnackbar(SUCCESS_CLEAR_FAVORITES, {variant: 'success'});
      onRequestComplete();
    })
    .catch(_err => {
      enqueueSnackbar(ERROR, {variant: 'error'});
    });
}

export function favoriteAsset(
  token: string,
  asset: SearchAssetInformationDto,
  onRequestComplete: () => void,
) {
  const data: AssetFavoriteRequestDto = {
    assetId: asset.id,
  };

  post(`${API_BASE_URL}/asset/favorite`, {
    data: data,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(_res => {
      enqueueSnackbar(ASSET_FAVORITE_SUCCESS, {variant: 'success'});
      onRequestComplete();
    })
    .catch(_err => {
      enqueueSnackbar(ERROR, {variant: 'error'});
    });
}

export function unfavoriteAsset(
  token: string,
  asset: SearchAssetInformationDto,
  onRequestComplete: () => void,
) {
  const data: AssetFavoriteRequestDto = {
    assetId: asset.id,
  };

  del(`${API_BASE_URL}/asset/favorite`, {
    data: data,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  })
    .then(_res => {
      enqueueSnackbar(ASSET_UNFAVORITE_SUCCESS, {
        variant: 'success',
      });
      onRequestComplete();
    })
    .catch(_err => {
      enqueueSnackbar(ERROR, {variant: 'error'});
    });
}

export function hasFavorites(
  token: string | undefined,
): Promise<AxiosResponse<HasAssetFavoritesResponseDto>> {
  return get(`${API_BASE_URL}/asset/favorite/summary`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'json',
  });
}
